import { createSelector } from "@reduxjs/toolkit";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import {
  getManifestSelector,
  getNodeAncestorIdsCreate,
  getNodeSlotIdSelector,
  getParentContextByIdSelector,
} from "../../state/index.ts";
import type { DesignerNode } from "../../types/index.ts";
import { resolveActiveNodesSelector } from "../querySelectors/index.ts";
import { getNodeAvailableActionsCreate } from "./getAvailableActions.ts";
import { resolveActiveNodeAvailableActionsCreate } from "./resolveActiveNodeAvailableActions.ts";
import type { InvokableAction } from "./shared.ts";

const getNodeAvailableActionsSelector = createSelector(
  [getManifestSelector, getParentContextByIdSelector],
  (getManifest, getParentContext) =>
    getNodeAvailableActionsCreate({
      getManifest,
      getParentContext,
    }),
);

const getNodeAncestorIdsSelector = createSelector([getNodeSlotIdSelector], (getSlotId) =>
  getNodeAncestorIdsCreate({ getSlotId }),
);

const resolveAvailableActionsSelector = createSelector(
  [getNodeAvailableActionsSelector, resolveActiveNodesSelector, getNodeAncestorIdsSelector],
  (getAvailableActions, resolveActiveNodes, getNodeAncestorIds) =>
    resolveActiveNodeAvailableActionsCreate({ getAvailableActions, resolveActiveNodes, getNodeAncestorIds }),
);

// *******************
// TODO: tidy up the naming is getting muddled

export type ResolveInvokableActions = ReturnType<typeof resolveAvailableActionsSelector>;

export interface TitledInvokableAction extends InvokableAction {
  title: string;
  payloadRequired?: boolean;
}

interface GetTitleInvokablesCreateOptions {
  resolveInvokableActions: ResolveInvokableActions;
}

interface GetTitleInvokablesOptions {
  excludePayloadRequired?: boolean;
}

export const getTitledInvokableActionsCreate =
  ({ resolveInvokableActions }: GetTitleInvokablesCreateOptions) =>
  (nodeOrId: Pick<DesignerNode, "id"> | number, { excludePayloadRequired }: GetTitleInvokablesOptions = {}) => {
    const actions = resolveInvokableActions(typeof nodeOrId === "number" ? nodeOrId : nodeOrId.id);
    if (excludePayloadRequired) {
      return actions.filter((a) => !a.payloadRequired) as TitledInvokableAction[];
    }
    return actions as TitledInvokableAction[];
  };

export type GetTitledInvokableActions = ReturnType<typeof getTitledInvokableActionsCreate>;

export const useGetInvokableActions = () => {
  const resolveInvokableActions = useSelector(resolveAvailableActionsSelector);
  return useMemo(() => getTitledInvokableActionsCreate({ resolveInvokableActions }), [resolveInvokableActions]);
};
